import React from 'react';
import { Box, Typography } from '@mui/material';
import { ErrorOutline as ErrorIcon, InboxOutlined as EmptyIcon, AccessTime as ClockIcon } from '@mui/icons-material';
import { ChartSkeleton } from '../../../components/enterprise';

/**
 * Standardised wrapper for a widget's three non-render states: loading,
 * error, and empty. Centralising these means every chart type renders the
 * same skeleton/error/empty experience instead of each chart inventing its
 * own (which the audit found — 19 chart files all freelancing).
 *
 * Usage:
 *   <WidgetState loading={loading} error={error} isEmpty={!data?.length}>
 *     <Recharts.LineChart … />
 *   </WidgetState>
 */
export const WidgetState = ({
  loading,
  error,
  isEmpty,
  emptyHint,
  errorAction,
  children,
}) => {
  if (loading) {
    // First paint of a widget: skeleton reserves the area (no spinner flash).
    return <ChartSkeleton height="100%" />;
  }
  if (error) {
    return (
      <CenteredBox>
        <ErrorIcon sx={{ fontSize: 28, color: 'error.main', mb: 0.5 }} />
        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, textAlign: 'center', px: 2 }}>
          {error.title || 'Query failed'}
        </Typography>
        {error.detail && (
          <Typography
            variant="caption"
            sx={{ mt: 0.5, color: 'text.secondary', textAlign: 'center', maxWidth: 480, px: 2,
              fontFamily: 'ui-monospace, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {error.detail}
          </Typography>
        )}
        {errorAction}
      </CenteredBox>
    );
  }
  if (isEmpty) {
    return (
      <CenteredBox>
        <EmptyIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          No data in this range
        </Typography>
        {emptyHint && (
          <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', textAlign: 'center', maxWidth: 480, px: 2 }}>
            {emptyHint}
          </Typography>
        )}
        <Typography variant="caption" sx={{ mt: 1, color: 'text.disabled', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <ClockIcon sx={{ fontSize: 12 }} /> Try widening the time range
        </Typography>
      </CenteredBox>
    );
  }
  return children;
};

const CenteredBox = ({ children }) => (
  <Box sx={{
    width: '100%', height: '100%', minHeight: 120,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    p: 1,
  }}>
    {children}
  </Box>
);

/**
 * Helper: turn the dashboards backend error response into the {title, detail}
 * shape the WidgetState wants. Exposes a friendly hint when the failure is
 * a known one (sandbox violation, missing time column).
 */
export const normaliseQueryError = (err) => {
  if (!err) return null;
  if (typeof err === 'string') return { title: 'Query failed', detail: err };
  const msg = err.message || err.error || String(err);
  if (/Access to table .* is not allowed/i.test(msg)) {
    return { title: 'Table not allowed', detail: msg };
  }
  if (/Only SELECT queries are allowed/i.test(msg)) {
    return { title: 'Only SELECT is allowed', detail: msg };
  }
  if (/needs a time column|no time column/i.test(msg)) {
    return {
      title: 'Missing time column',
      detail: 'Add a column like toStartOfMinute(timestamp) AS ts and a GROUP BY ts to render this chart.',
    };
  }
  return { title: 'Query failed', detail: msg };
};

export default WidgetState;
